import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useContext, useEffect, useState } from "react";
import { getFormEntriesByFormName } from "../services/formServices";
import { FORMNAMES } from "../utilities/codes";
import toast from "react-hot-toast";
import { schoolContext } from "../contexts/SchoolContextProvider";
import { isVideo, mediaSource, useBackgroundImage } from "../utilities/mediautilities";
import { useTranslation } from "react-i18next";
import { I18N_KEYS } from "../languages/PlaceHolder.js";

const Gallery = () => {
    const { schoolDetails } = useContext(schoolContext);
    const [activeCategory, setActiveCategory] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [selectedCampusMedia, setSelectedCampusMedia] = useState(null);
    const [gallery, setGallery] = useState([]);
    const [slideShowMedia, setSlideShowMedia] = useState([]);
    const { t } = useTranslation()
    const bgUrl = useBackgroundImage(schoolDetails?.gallery?.fileUrl);

    useEffect(() => {
        getGallery();
    }, [])

    useEffect(() => {
        const images = gallery?.map((gl) => {
            if (gl?.payload?.category === activeCategory) return gl?.payload?.images;
        }).filter(Boolean);
        setSlideShowMedia(images[0]);
    }, [activeCategory])

    const getGallery = async () => {
        try {
            const response = await getFormEntriesByFormName(FORMNAMES.SCHOOLGALLERY);
            const map = new Map();

            response?.forEach(item => {
                const category = item?.payload?.category;
                if (!map.has(category)) {
                    map.set(category, { ...item, payload: { ...item?.payload, images: [...(item?.payload?.images || [])], }, });
                } else {
                    map.get(category).payload?.images.push(...(item?.payload?.images || []));
                }
            });

            const updatedResponse = [...map.values()];
            setGallery(updatedResponse);
            setActiveCategory(updatedResponse[0]?.payload?.category === "campus" ? updatedResponse[1]?.payload?.category : updatedResponse[0]?.payload?.category)
        } catch (error) {
            toast.error(error?.message);
        }
    }

    const nextImage = () => {
        setSelectedIndex((prev) =>
            prev === slideShowMedia?.length - 1 ? 0 : prev + 1
        );
    };

    const prevImage = () => {
        setSelectedIndex((prev) =>
            prev === 0 ? slideShowMedia?.length - 1 : prev - 1
        );
    };

    return (
        <>
            {/* Hero Section */}
            <section
                className="relative bg-cover bg-center py-20 px-6"
                style={{ backgroundImage: `url(${bgUrl})` }}
            >
                <div className="absolute inset-0 bg-black/50"></div>
                <div className="relative z-10 common-container">
                    <div className=" rounded-lg text-left">
                        <h1 className="text-4xl md:text-5xl font-bold mb-4 text-accent">
                            {t(I18N_KEYS.GALERYPAGENAME)}
                        </h1>
                        <p className="text-lg md:text-xl text-accent">
                            <Link to="/" className="hover:text-primary transition-colors">
                                {t(I18N_KEYS.HOMEPAGENAME)}
                            </Link>{" "}
                            &gt; {t(I18N_KEYS.GALERYPAGENAME)}
                        </p>
                    </div>
                </div>
            </section>

            {/* Campus Photos Section */}
            <section className="py-16 px-6 bg-accent">
                <motion.h2
                    className=" text-3xl font-bold text-primary text-center mb-10"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.6 }}
                >
                    {t(I18N_KEYS.CAMPUSPHOTOTITLE)}
                </motion.h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 common-container">
                    {gallery?.filter((x) => x?.payload?.category === "campus").map((gl, i) => (
                        gl?.payload?.images.map((media, idx) => (
                            <motion.a
                                key={idx}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="relative rounded-xl overflow-hidden shadow-lg cursor-pointer group"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: i * 0.1 }}
                                onClick={() => setSelectedCampusMedia(media)}
                            >
                                <div
                                    className="h-64 transform transition-transform duration-500 group-hover:scale-105"
                                >
                                    {isVideo(media?.fileName) ? (
                                        <video
                                            controls
                                            controlsList="nodownload"
                                            className="h-full w-full object-cover"
                                        >
                                            <source src={mediaSource(media?.fileUrl)} onError={(e) => mediaSource(e)}/>
                                        </video>
                                    ) : (
                                        <img
                                            src={mediaSource(media?.fileUrl)}
                                            alt=""
                                            className="h-full w-full"
                                            onError={(e) => mediaSource(e)}
                                        />
                                    )}
                                </div>
                            </motion.a>
                        ))
                    ))}
                </div>
            </section>
            {/* Lightbox / Modal */}
            {selectedCampusMedia &&
                <AnimatePresence>
                    <motion.div
                        className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setSelectedCampusMedia(null)}
                    >
                        {isVideo(selectedCampusMedia?.fileName) ? (
                            <motion.video
                                controls
                                controlsList="nodownload"
                                alt="Campus"
                                className="max-h-full max-w-full rounded-lg shadow-2xl"
                                initial={{ scale: 0.8 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0.8 }}
                                transition={{ duration: 0.3 }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <source src={mediaSource(selectedCampusMedia?.fileUrl)} onError={(e) => mediaSource(e)}/>
                            </motion.video>
                        ) : (
                            <motion.img
                                src={mediaSource(selectedCampusMedia?.fileUrl)}
                                alt="Campus"
                                className="max-h-full max-w-full rounded-lg shadow-2xl"
                                initial={{ scale: 0.8 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0.8 }}
                                transition={{ duration: 0.3 }}
                                onClick={(e) => e.stopPropagation()}
                                onError={(e) => mediaSource(e)}
                            />
                        )}

                        <button
                            onClick={() => setSelectedCampusMedia(null)}
                            className="absolute top-6 right-6 text-white text-3xl font-bold hover:text-primary"
                        >
                            &times;
                        </button>
                    </motion.div>
                </AnimatePresence>
            }

            {/* Main Gallery Section */}
            <section id="gallery" className="py-20 px-6 bg-gray-50">
                <motion.h2
                    className="text-4xl font-bold text-primary text-center mb-12"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.6 }}
                >
                    {t(I18N_KEYS.GALLERYTITLE)}
                </motion.h2>

                <div className="flex justify-center gap-4 mb-12 flex-wrap">
                    {gallery?.filter((x) => x?.payload?.category !== "campus").map((gl) => (
                        <button
                            key={gl?.payload?.category}
                            onClick={() => setActiveCategory(gl?.payload?.category)}
                            className={`px-6 py-2 rounded-full font-semibold transition-all
                                ${activeCategory === gl?.payload?.category
                                    ? "bg-primary text-accent"
                                    : "bg-accent text-primary border hover:bg-primary hover:text-accent"
                                }`}
                        >
                            {gl?.payload?.category?.toUpperCase()}
                        </button>
                    ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {gallery?.filter((gl) => gl?.payload?.category === activeCategory).map((gl, i) => (
                        gl?.payload?.images.map((media, idx) => (
                            <motion.div
                                key={idx}
                                className="relative rounded-xl overflow-hidden shadow-lg cursor-pointer group"
                                initial={{ opacity: 0, y: 40 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                onClick={() => setSelectedIndex(idx)}
                            >
                                <div
                                    className="h-64 bg-center bg-cover transform transition-transform duration-500 group-hover:scale-110"
                                >
                                    {isVideo(media?.fileName) ? (
                                        <video
                                            controls
                                            controlsList="nodownload"
                                            className="h-full w-full object-cover"
                                        >
                                            <source src={mediaSource(media?.fileUrl)} onError={(e) => mediaSource(e)}/>
                                        </video>
                                    ) : (
                                        <img
                                            src={mediaSource(media?.fileUrl)}
                                            alt=""
                                            className="h-full w-full"
                                            onError={(e) => mediaSource(e)}
                                        />
                                    )}
                                </div>

                                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                    <p className="text-accent text-lg font-semibold">View{" "}{isVideo(media?.fileName) ? "video" : "image"}</p>
                                </div>
                            </motion.div>
                        ))
                    ))}
                </div>

            </section>



            <AnimatePresence>
                {selectedIndex !== null && (
                    <motion.div
                        className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setSelectedIndex(null)}
                    >
                        {/* Close */}
                        <button
                            className="absolute top-6 right-6 text-white text-4xl font-bold z-50"
                            onClick={() => setSelectedIndex(null)}
                        >
                            &times;
                        </button>

                        {/* Previous */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                prevImage();
                            }}
                            className="absolute left-6 text-white text-5xl font-bold z-50 hover:text-primary"
                        >
                            &#8249;
                        </button>

                        {/* Image */}
                        {isVideo(slideShowMedia[selectedIndex]?.fileName) ? (
                            <motion.video
                                controls
                                controlsList="nodownload"
                                key={selectedIndex}
                                alt="Gallery"
                                className="max-h-[80vh] max-w-[90vw] rounded-xl shadow-2xl"
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.8, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <source src={mediaSource(slideShowMedia[selectedIndex]?.fileUrl)} onError={(e) => mediaSource(e)}/>
                            </motion.video>
                        ) : (
                            <motion.img
                                key={selectedIndex}
                                src={mediaSource(slideShowMedia[selectedIndex]?.fileUrl)}
                                alt="Gallery"
                                className="max-h-[80vh] max-w-[90vw] rounded-xl shadow-2xl"
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.8, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                onClick={(e) => e.stopPropagation()}
                                onError={(e) => mediaSource(e)}
                            />
                        )}

                        {/* Next */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                nextImage();
                            }}
                            className="absolute right-6 text-white text-5xl font-bold z-50 hover:text-primary"
                        >
                            &#8250;
                        </button>

                        {/* Thumbnails Slider */}
                        <div
                            className="absolute bottom-6 flex gap-3 px-4 max-w-full"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {slideShowMedia?.map((media, i) =>
                                isVideo(media?.fileName) ? (
                                    <video
                                        onClick={() => setSelectedIndex(i)}
                                        key={i}
                                        className={`h-16 w-24 object-cover rounded-lg cursor-pointer border-2 transition
                                        ${i === selectedIndex
                                                ? "border-primary scale-110"
                                                : "border-transparent opacity-70 hover:opacity-100"
                                            }`}
                                    >
                                        <source src={mediaSource(media?.fileUrl)} onError={(e) => mediaSource(e)} />
                                    </video>
                                ) : (
                                    <img
                                        key={i}
                                        src={mediaSource(media?.fileUrl)}
                                        onClick={() => setSelectedIndex(i)}
                                        className={`h-16 w-24 object-cover rounded-lg cursor-pointer border-2 transition
                                        ${i === selectedIndex
                                                ? "border-primary scale-110"
                                                : "border-transparent opacity-70 hover:opacity-100"
                                            }`}
                                        onError={(e) => mediaSource(e)}
                                    />
                                ),
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

        </>
    );
}
export default Gallery;